// src/shared/utils/resellerBrand.ts
//
// Post-login brand identity: Streamhoster (direct) vs white-label reseller
// (themediacdn). Decided from the ACCOUNT, never from the login hostname —
// the same customer can sign in at my.streamhoster.com or my.themediacdn.com,
// and embeds, hosts, and external-platform app ids must not flip between the
// two per visit (legacy `isResellerExperience`). The pre-login screens, where
// no account exists yet, use the hostname rule in `brand.ts` instead.

import { ServiceHosts } from '@lib2/model/common/ServiceHosts';
import type { CustomercfgResellerData } from '@/lib/types/Customer';

export class ResellerBrand {
  /**
   * A customer gets the reseller experience when any reseller config field is
   * populated. The backend patches the parent's reseller config into a
   * sub-account's own cfg (sub-account logins and the account selector's
   * sub-account rows alike), so the selected customer's `cfg.reseller` is
   * authoritative for every account shape.
   */
  static isReseller(reseller: CustomercfgResellerData | undefined | null): boolean {
    if (!reseller) return false;
    return !!(
      reseller.supportUri ||
      reseller.supportPhone ||
      reseller.logoFileId ||
      reseller.logoFormat
    );
  }

  /** Content-service host for embeds, previews, downloads, and feeds. */
  static contentHost(isReseller: boolean): string {
    return isReseller ? ServiceHosts.content.reseller : ServiceHosts.content.streamhoster;
  }

  /**
   * Re-brand a platform service URL for the reseller experience by swapping
   * the Streamhoster domain for the white-label one (legacy Encoder Setup did
   * exactly this to the RTMP server URL; the white-label domain aliases the
   * same hosts). A direct customer's URL is returned unchanged.
   */
  static rewriteServiceUrl(url: string, isReseller: boolean): string {
    if (!isReseller) return url;
    return url.replace(ServiceHosts.hosts.streamhoster, ServiceHosts.hosts.reseller);
  }
}
